import { CoreAPI, APICallOptions } from '../core-api';
import { HeroAPIs, getAPIEndpoint, API } from '@hero/api';
import { z } from 'zod';

/**
 * Type for API method - explicitly defines only the properties we want to expose
 * Function prototype methods (bind, call, apply, etc.) will still exist but
 * won't be as prominent in IntelliSense
 */
type APIMethod<T extends API<any, any>> = {
  (data: T['req'], options?: APICallOptions): Promise<T['data']>;
  /** the api schema for the request */
  schema: z.ZodSchema<any> | undefined;
  /** the api with the full response (success, message, statusCode, etc.) */
  withRes: (data: T['req'], options?: APICallOptions) => Promise<T['res']>;
};

/**
 * Create an API method that exposes both schema and callable function.
 * The schema property will have proper TypeScript types for IntelliSense.
 *
 * @example
 * ```typescript
 * const getOrders = createAPIMethod<BusinessOrdersAPI>(coreAPI, "order.businessOrders");
 *
 * // Access the schema for form validation
 * const schema = getOrders.schema; // TypeScript knows the exact schema type
 *
 * // Call the API - returns just the data
 * const data = await getOrders({ page: 1, limit: 10 }); // Returns BusinessOrdersAPI['data']
 *
 * // Call with options (headers, timeout, etc.)
 * const dataWithOptions = await getOrders(
 *   { page: 1, limit: 10 },
 *   { headers: { 'X-Custom-Header': 'value' }, timeout: 5000 }
 * );
 *
 * // Call with full response - returns the full Res object (with success, message, statusCode, etc.)
 * const response = await getOrders.withRes({ page: 1, limit: 10 }); // Returns BusinessOrdersAPI['res']
 *
 * // Call withRes with options
 * const responseWithOptions = await getOrders.withRes(
 *   { page: 1, limit: 10 },
 *   { headers: { 'X-Custom-Header': 'value' } }
 * );
 * ```
 */
export function createAPIMethod<T extends API<any, any>>(
  coreAPI: CoreAPI,
  apiName: HeroAPIs,
): APIMethod<T> {
  const route = getAPIEndpoint(apiName);

  // Extract function name from API name (e.g., "order.getOne" -> "getOne")
  const funcName = apiName.includes('.') ? apiName.split('.').pop()! : apiName;

  const method = async (data: T['req'], options?: APICallOptions): Promise<T['data']> => {
    // Pass logging metadata through options (will be stripped before API request)
    return coreAPI.call<T>(route, data, {
      ...options,
      __logging: {
        apiName,
        funcName,
      },
    });
  };

  // Attach schema to the method object with proper typing
  Object.defineProperty(method, 'schema', {
    get: () => route.schema,
    enumerable: true,
    configurable: true,
  });

  // Attach withRes method to return full response
  Object.defineProperty(method, 'withRes', {
    value: async (data: T['req'], options?: APICallOptions): Promise<T['res']> => {
      // Pass logging metadata through options (will be stripped before API request)
      return coreAPI.callWithRes<T>(route, data, {
        ...options,
        __logging: {
          apiName,
          funcName,
        },
      });
    },
    enumerable: true,
    configurable: true,
  });

  return method as APIMethod<T>;
}
